import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { GraduationCap, Search } from 'lucide-react';
import { SideDrawer, ProgressBar } from '../SideDrawer';
import { Spinner, ErrorBanner, PaginationBar } from '../ReportTableKit';

const API = process.env.REACT_APP_BACKEND_URL;
const TABS = [['enrolled', 'Enrolled'], ['completed', 'Completed'], ['vouchers', 'Vouchers']];
const dash = v => (v === null || v === undefined || v === '' ? '—' : v);
const BASE_COLS = [['lms_id', 'LMS ID'], ['name', 'Name'], ['email', 'Email'], ['partner_name', 'Partner'],
  ['domain_type', 'Domain Type'], ['region', 'Region'], ['language', 'Language']];
const KIND_COLS = {
  enrolled: [['enrolled_at', 'Enrolled'], ['progress_pct', 'Progress'], ['completed_at', 'Completed']],
  completed: [['enrolled_at', 'Enrolled'], ['completed_at', 'Completed']],
  vouchers: [['assigned_at', 'Assigned'], ['voucher_type', 'Voucher Type'], ['status', 'Status']],
};

function LearnerRows({ rows, kind }) {
  const cols = [...BASE_COLS, ...KIND_COLS[kind]];
  if (!rows.length) return <div className="empty-state" data-testid="course-learners-empty"><h3>No learners found</h3></div>;
  return (
    <div className="table-container"><div className="table-scroll" style={{ overflowX: 'auto' }}>
      <table data-testid="course-learners-table">
        <thead><tr>{cols.map(([k, l]) => <th key={k} style={{ whiteSpace: 'nowrap' }}>{l}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.lms_id}-${i}`} data-testid={`course-learner-row-${i}`} style={{ fontSize: 12 }}>
              {cols.map(([k]) => (
                <td key={k} style={{ whiteSpace: ['name', 'email', 'partner_name', 'domain_type'].includes(k) ? 'normal' : 'nowrap', wordBreak: k === 'email' ? 'break-all' : undefined, maxWidth: k === 'email' ? 180 : undefined }}>
                  {k === 'progress_pct' && r[k] !== null ? <ProgressBar pct={r[k]} /> : dash(r[k])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div></div>
  );
}

export function CourseLearnersDrawer({ course, filters, onClose }) {
  const [kind, setKind] = useState('enrolled');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { const t = setTimeout(() => { setQuery(q); setPage(1); }, 400); return () => clearTimeout(t); }, [q]);

  useEffect(() => {
    let live = true;
    setData(d => (d ? { ...d, loading: true } : d)); setError(null);
    axios.get(`${API}/api/cert-prep/v2/course-learners`, {
      params: { ...filters, ...(course.domain ? { domain: course.domain } : { exam_code: course.label }), kind, q: query || undefined, page, per_page: 25 }, withCredentials: true,
    }).then(r => { if (live) setData(r.data); }).catch(() => { if (live) setError('Could not load learners.'); });
    return () => { live = false; };
  }, [course.label, course.domain, filters, kind, query, page]);

  return (
    <SideDrawer testId="course-learners-drawer" icon={GraduationCap} kicker={`${filters.campaign} · ${filters.date_from} → ${filters.date_to}`}
      title={course.name || course.label} width={1100} onClose={onClose}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => { setKind(k); setPage(1); }} data-testid={`course-learners-tab-${k}`}
            className={kind === k ? 'btn btn-primary' : 'btn btn-secondary'} style={{ padding: '6px 14px', fontSize: 13 }}>
            {l} {data?.counts ? `(${data.counts[k].toLocaleString()})` : ''}
          </button>
        ))}
        <div style={{ position: 'relative', marginLeft: 'auto' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input className="filter-input" value={q} onChange={e => setQ(e.target.value)} data-testid="course-learners-search"
            placeholder={data?.pii_allowed === false ? 'Search name or LMS ID…' : 'Search name, email or LMS ID…'} style={{ paddingLeft: 30, width: 260 }} />
        </div>
      </div>
      {error && <ErrorBanner message={error} testId="course-learners-error" />}
      {!data && !error && <Spinner />}
      {data && (
        <div style={{ opacity: data.loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
          <LearnerRows rows={data.data} kind={data.kind} />
          {data.total > 0 && <PaginationBar page={data.page} totalPages={data.total_pages} total={data.total} perPage={25} onPage={setPage} testId="course-learners-pagination" />}
        </div>
      )}
    </SideDrawer>
  );
}
